import styled from 'foundations-theming/utils';
import { useEffect, useRef } from 'react';

// Offset to prevent the drag item from being too close to the cursor. We
// need this offset because overlap with the cursor dnd since the drag item
// is simultaneously at a higher z-index than the overlays and gridlines.
import { jsx as _jsx } from "react/jsx-runtime";
const DRAG_PREVIEW_CURSOR_OFFSET = 3;
const ShadowedDragPreview = styled.div.withConfig({
  displayName: "DragPreview__ShadowedDragPreview"
})([".hs-private.hs-private.hs-private &{position:absolute;background-color:", ";}"], ({
  theme
}) => theme.color['fill-tertiary-pressed']);
const useScrollPosition = () => {
  const scrollPositionRef = useRef({
    initialLeft: window.scrollX,
    initialTop: window.scrollY,
    left: 0,
    top: 0
  });
  useEffect(() => {
    const handleScroll = () => {
      scrollPositionRef.current.left = window.scrollX - scrollPositionRef.current.initialLeft;
      scrollPositionRef.current.top = window.scrollY - scrollPositionRef.current.initialTop;
    };
    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);
  return scrollPositionRef.current;
};
const useElementSizeAndPosition = element => {
  const elementSizeRef = useRef({
    width: 0,
    height: 0,
    left: 0,
    top: 0
  });
  useEffect(() => {
    if (element) {
      const rect = element.getBoundingClientRect();
      elementSizeRef.current.left = rect.left;
      elementSizeRef.current.top = rect.top;
      elementSizeRef.current.width = rect.width;
      elementSizeRef.current.height = rect.height;
    }
  }, [element]);
  return elementSizeRef.current;
};
/**
 * Renders a shadowed drag preview of the element to be dragged, with the same
 * size and position as the dragged element. The dragged element is updated to
 * then follow the cursor position when the cursor is moved.
 */
const DragPreview = ({
  clientOffset,
  element
}) => {
  const {
    width,
    height,
    left: elementLeft,
    top: elementTop
  } = useElementSizeAndPosition(element);
  const {
    left: scrollLeft,
    top: scrollTop,
    initialLeft: initialScrollLeft,
    initialTop: initialScrollTop
  } = useScrollPosition();

  // Updates the dragged element to follow the cursor position when the cursor
  // is moved. It's adjusted to account for a cursor offset and scroll position.
  useEffect(() => {
    if (element && clientOffset) {
      const x = clientOffset.x + DRAG_PREVIEW_CURSOR_OFFSET + scrollLeft - elementLeft - width / 2;
      const y = clientOffset.y + DRAG_PREVIEW_CURSOR_OFFSET + scrollTop - elementTop;
      element.style.transform = `translate(${x}px, ${y}px)`;
      element.style.zIndex = '2147483599'; // Set the z-index to be above overlays and gridlines
      element.style.opacity = '0.8';
    }
    return () => {
      if (element) {
        element.style.removeProperty('z-index');
        element.style.removeProperty('transform');
        element.style.removeProperty('opacity');
      }
    };
  }, [element, clientOffset, scrollLeft, scrollTop, elementLeft, elementTop, width]);
  const styles = {
    width: `${width}px`,
    height: `${height}px`,
    left: `${elementLeft + initialScrollLeft}px`,
    top: `${elementTop + initialScrollTop}px`
  };
  return (
    /*#__PURE__*/
    // @bend-expect trellis-tokenized
    _jsx("div", {
      className: "hs-private",
      children: /*#__PURE__*/_jsx(ShadowedDragPreview, {
        style: styles,
        "data-test-id": "drag-preview"
      })
    })
  );
};
export default DragPreview;